import React, { ErrorInfo } from 'react';
import { Route, Switch } from 'react-router-dom';
import * as ReactDOM from 'react-dom';
import { initCsrfConfig } from './main/api';

import './style.scss';
import { FacebookProvider } from 'react-facebook';
import { locatePath } from './common/RelativeUrl';
import { SheRavenClient } from './commonv2/modules/ravenClient';
import { initCsrfConfigV2 } from './commonv2/api/ApiV2';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import { Router, Redirect } from 'react-router';
import { history, routingStore } from './common/stores/routing';
import { scrollToTop } from './mainv2/utils/scroll';
import smoothscroll from 'smoothscroll-polyfill';
import { TrialLp } from './mainv2/containers/TrialLandingPage';
import { initCsrfConfigV3 } from './commonv3/api/ApiV3';
import { experimentState } from './commonv2/states/globalStates';
import { RUNNING_EXPERIMENTS } from './mainv3/experiments';
import { SheEventClient } from './commonv3/events/SheEventClient';
import { v2Path } from './mainv2/routes';
import { exposeSurveyFunctions } from './mainv3/apis/surveySubmissions';
import { lazyPage } from './commonv3/utils/lazy';
const TestVisitorErrorPage = lazyPage(() => import('./mainv3/containers/test/TestPages'));
const MultiCreatorTrialLessonListPage = lazyPage(() =>
    import('./mainv3/containers/trial/MultiCreatorTrialLessonListPage')
);
const MultiCreatorTrialLessonReservationCompletePage = lazyPage(() =>
    import('./mainv3/containers/trial/MultiCreatorTrialLessonReservationCompletePage')
);
import { initTime } from './commonv3/utils/time';
const TrialLessonListPage = lazyPage(() => import('./mainv3/containers/trial/TrialLessonListPage'));
import { exposeFbq } from './adminv3/api/facebook';

const MainAppV3 = lazyPage(() => import('./mainv3/MainAppV3'));
const TrialSignInPage = lazyPage(() => import('./mainv2/containers/sign_in/TrialSignInPage'));
const TrialFaqPage = lazyPage(() => import('./mainv3/containers/trial_faq/TrialFaqPage'));
const DiagnosisPage = lazyPage(() => import('./mainv2/containers/diagnosis/DiagnosisPage'));
const DiagnosisResultPage = lazyPage(() => import('./mainv2/containers/diagnosis/DiagnosisResultPage'));
const LocalTrialLandingPage = lazyPage(() => import('./mainv3/containers/trial6/LocalTrialLandingPage'));
const TrialLessonReservationCompletePage = lazyPage(() =>
    import('./mainv3/containers/trial/TrialLessonReservationCompletePage')
);
const TrialLessonReschedulePage = lazyPage(() => import('./mainv3/containers/trial/TrialLessonReschedulePage'));
const TrialLessonRescheduleCompletePage = lazyPage(() =>
    import('./mainv3/containers/trial/TrialLessonRescheduleCompletePage')
);

// Injected from the view
declare let FACEBOOK_APP_ID: string;
@observer
class App extends React.Component<any> {
    private unsubscriber: () => any = null;

    async componentDidMount() {
        initTime();
        SheRavenClient.init();
        exposeSurveyFunctions();
        exposeFbq();
        smoothscroll.polyfill();
        this.unsubscriber = reaction(
            () => routingStore.location.pathname,
            () => {
                scrollToTop();
                SheEventClient.reportView();
            }
        );
        await experimentState.init(RUNNING_EXPERIMENTS);
    }

    componentWillUnmount() {
        this.unsubscriber();
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        SheRavenClient.captureException(error, { extra: errorInfo });
    }

    render() {
        return (
            <FacebookProvider appId={FACEBOOK_APP_ID} language="ja_JP">
                <Router history={history}>
                    <Switch>
                        <Route exact path={locatePath('')} component={TrialLp} />
                        <Route exact path={locatePath('fv')} component={TrialLp} />
                        <Route exact path={locatePath('trial/document_01')} component={TrialLp} />
                        <Route exact path={locatePath('trial')} component={TrialLp} />
                        <Route exact path={locatePath('trial8')}>
                            <Redirect to={v2Path('trial')} />
                        </Route>
                        <Route exact path={locatePath('trial/nagoya')}>
                            <Redirect to={v2Path('trial')} />
                        </Route>
                        <Route exact path={locatePath('sign_in_')} component={TrialSignInPage} />
                        <Route exact path={locatePath('trial')} component={TrialLp} />
                        <Route exact path={locatePath('local_trial')} component={LocalTrialLandingPage} />
                        <Route exact path={locatePath('trial/faq')} component={TrialFaqPage} />
                        <Route exact path={locatePath('diagnosis')} component={DiagnosisPage} />
                        <Route exact path={locatePath('diagnosis/:code')} component={DiagnosisResultPage} />
                        <Route exact path={v2Path('trial_lessons')} component={TrialLessonListPage} />
                        <Route exact path={v2Path('trial_lessons8')}>
                            <Redirect to={v2Path('trial_lessons')} />
                        </Route>
                        <Route exact path={v2Path('trial_lessons/nagoya')}>
                            <Redirect to={v2Path('trial_lessons')} />
                        </Route>
                        <Route
                            exact
                            path={v2Path('trial_lessons/multicreator')}
                            component={MultiCreatorTrialLessonListPage}
                        />
                        <Route
                            exact
                            path={v2Path('trial_lessons/:lessonId/reservation_complete')}
                            component={TrialLessonReservationCompletePage}
                        />
                        <Route
                            exact
                            path={v2Path('trial_lessons/:lessonId/reschedule_complete')}
                            component={TrialLessonRescheduleCompletePage}
                        />
                        <Route
                            exact
                            path={v2Path('trial_lessons/multicreator/:lessonId/reservation_complete')}
                            component={MultiCreatorTrialLessonReservationCompletePage}
                        />
                        <Route
                            exact
                            path={v2Path('trial_reservations/:reservationId/reschedule')}
                            component={TrialLessonReschedulePage}
                        />
                        <Route path={locatePath('/v3')} component={MainAppV3} />
                        <Route exact path="/test_always_fail" component={TestVisitorErrorPage} />
                        <Route path="/" component={MainAppV3} />
                    </Switch>
                </Router>
            </FacebookProvider>
        );
    }
}

document.addEventListener('DOMContentLoaded', () => {
    initCsrfConfig();
    initCsrfConfigV2();
    initCsrfConfigV3();
    ReactDOM.render(<App />, document.getElementById('content'));
});
